import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type InjurySeverity = "mild" | "moderate" | "severe";
export type InjuryBodyPart =
  | "leg" | "knee" | "shoulder" | "back" | "foot" | "ankle" | "hip" | "neck" | "other";

export type InjuryStatus = "open" | "healing" | "resolved";

export interface Injury {
  id: number;
  body_part: InjuryBodyPart;
  severity: InjurySeverity;
  started_at: string;
  resolved_at: string | null;
  recovery_pct: number | null; // effective % (resolved → 100)
  status: InjuryStatus;
  notes: string | null;
  created_at: string;
  is_open: boolean;
}

export interface InjuryInput {
  body_part: InjuryBodyPart;
  severity: InjurySeverity;
  started_at: string;
  resolved_at?: string | null;
  recovery_pct?: number | null;
  notes?: string | null;
}

export function useInjuries() {
  return useQuery<Injury[]>({
    queryKey: ["athlete-injuries"],
    queryFn: async () => {
      const r = await api.get<Injury[]>("/athlete/injuries");
      return r.data;
    },
    staleTime: 60_000,
  });
}

export function useCreateInjury() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: InjuryInput) => {
      const r = await api.post<Injury>("/athlete/injuries", vars);
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["athlete-injuries"] }),
  });
}

export function useUpdateInjury() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; patch: Partial<InjuryInput> }) => {
      const r = await api.patch<Injury>(`/athlete/injuries/${vars.id}`, vars.patch);
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["athlete-injuries"] }),
  });
}

export function useDeleteInjury() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/athlete/injuries/${id}`);
      return id;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["athlete-injuries"] }),
  });
}
